import type { MatrixFolder } from '@mss_creator/shared';
import { ActionMenu, Button, Icon } from '@pms-digital/design-system';

export interface FolderHeaderProps {
  folder: MatrixFolder;
  /** The notes panel of this folder is open. */
  notesOpen: boolean;
  onToggleNotes: () => void;
  onRename: (folder: MatrixFolder) => void;
  onEditDescription: () => void;
  onDelete: (folder: MatrixFolder) => void;
}

/**
 * The head of an open folder (issue #19, planche 15): its name, how many
 * matrices it holds, the « Notes » button with the open-notes counter, and the
 * « ⋯ » menu (Renommer, Modifier la description, Supprimer).
 */
export function FolderHeader({
  folder,
  notesOpen,
  onToggleNotes,
  onRename,
  onEditDescription,
  onDelete,
}: FolderHeaderProps) {
  return (
    <div className="flex items-center gap-2">
      <Icon name="folder" size={16} />
      <h2 className="min-w-0 break-words font-semibold text-noir text-small">{folder.name}</h2>
      <span className="shrink-0 text-caption text-gray-500">
        {folder.matrixCount > 1
          ? `${folder.matrixCount} matrices`
          : `${folder.matrixCount} matrice`}
      </span>
      <span className="flex-1" />
      <button
        type="button"
        aria-pressed={notesOpen}
        onClick={onToggleNotes}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-semibold text-bleu-nuit text-small hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit aria-pressed:bg-bleu-nuit-tint"
      >
        <Icon name="chat_bubble" size={16} />
        Notes
        {folder.openNoteCount > 0 && (
          <span className="rounded-full bg-gray-100 px-1 py-px font-normal text-caption text-gray-700">
            <span className="sr-only">Notes ouvertes : </span>
            {folder.openNoteCount}
          </span>
        )}
      </button>
      <ActionMenu
        align="end"
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            icon="more_horiz"
            aria-label={`Actions pour le dossier « ${folder.name} »`}
          />
        }
        items={[
          { label: 'Renommer', icon: 'edit', onSelect: () => onRename(folder) },
          { label: 'Modifier la description', icon: 'input', onSelect: onEditDescription },
          {
            label: 'Supprimer',
            icon: 'delete',
            destructive: true,
            onSelect: () => onDelete(folder),
          },
        ]}
      />
    </div>
  );
}
